import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';
import { faThumbsDown } from '@fortawesome/free-solid-svg-icons/faThumbsDown';
import { faThumbsUp as faThumbsUpRegular } from '@fortawesome/free-regular-svg-icons/faThumbsUp';
import { faThumbsDown as faThumbsDownRegular } from '@fortawesome/free-regular-svg-icons/faThumbsDown';
import { getLang } from 'shared/store/selectors/lang.selector';
import { FEEDBACK_VALUE } from 'shared/panda/panda.constants';
import type { FeedbackValue } from 'shared/panda/panda.constants';
import { useFeedbackToggle } from 'shared/panda/feedback/feedback-toggle.hooks';
import { IconButton } from 'src/components/design-system';
import { CopyToClipboard } from 'src/components/common/copy-to-clipboard';
import styles from './message-actions.module.scss';

export interface MessageActionsProps {
  copyText: string;
  /** Fired after the copy action succeeds. */
  onCopy: () => void;
  /** Fired with the new selection on every thumb toggle; `null` = unselected. */
  onFeedback: (value: FeedbackValue) => void;
}

export function MessageActions({ copyText, onCopy, onFeedback }: MessageActionsProps) {
  const lang = useSelector(getLang('AI_CHAT'));
  const { isPositive, isNegative, toggleFeedback } = useFeedbackToggle(onFeedback);

  return (
    <div className={styles.actions}>
      <CopyToClipboard
        text={copyText}
        onCopy={onCopy}
        label={lang.COPY_RESPONSE}
        className={styles.actionButton}>
        <FontAwesomeIcon icon={faCopy} className={styles.actionIcon} />
      </CopyToClipboard>

      <IconButton
        label={lang.GOOD_RESPONSE}
        aria-pressed={isPositive}
        className={classNames(styles.actionButton, isPositive && styles.selected)}
        onClick={() => toggleFeedback(FEEDBACK_VALUE.POSITIVE)}>
        <FontAwesomeIcon
          icon={isPositive ? faThumbsUp : faThumbsUpRegular}
          className={styles.actionIcon}
        />
      </IconButton>

      <IconButton
        label={lang.BAD_RESPONSE}
        aria-pressed={isNegative}
        className={classNames(styles.actionButton, isNegative && styles.selected)}
        onClick={() => toggleFeedback(FEEDBACK_VALUE.NEGATIVE)}>
        <FontAwesomeIcon
          icon={isNegative ? faThumbsDown : faThumbsDownRegular}
          className={styles.actionIcon}
        />
      </IconButton>
    </div>
  );
}
